/**
 * ConfirmationCard Component
 * Shows a confirmation dialog when the AI wants to perform an action
 * that requires user approval before execution.
 * Dates are collected in chat, not in this card.
 */

import React from 'react';
import { useAIContext } from './AIContext';
import { useThemeContext } from '../../contexts/ThemeContext';

const ConfirmationCard: React.FC = () => {
  const { pendingConfirmation, confirmAction, cancelAction, isProcessing } = useAIContext();
  const { isDark } = useThemeContext();

  if (!pendingConfirmation) return null;

  return (
    <div className="p-3 border-t" style={{ borderColor: 'var(--grey-light)' }}>
      <div
        className="rounded-xl p-4"
        style={{
          backgroundColor: 'var(--bg-grey-light)',
          border: '1px solid',
          borderColor: 'var(--secondary)',
        }}
      >
        <div className="flex items-center justify-between gap-2 mb-3">
          <span className="font-semibold text-sm" style={{ color: 'var(--grey-dark)' }}>
            {pendingConfirmation.title || 'Confirm'}
          </span>
          {pendingConfirmation.booksName && (
            <span className="text-[11px] truncate opacity-70" style={{ color: 'var(--grey-dark)' }}>
              {pendingConfirmation.booksName}
            </span>
          )}
        </div>

        <div
          className="text-sm mb-3 p-3 rounded-lg whitespace-pre-wrap"
          style={{
            backgroundColor: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.7)',
            color: 'var(--dark)',
          }}
        >
          {pendingConfirmation.sentence || pendingConfirmation.humanReadableAction}
        </div>

        {pendingConfirmation.newTerms && pendingConfirmation.newTerms.length > 0 && (
          <p className="text-xs mb-3" style={{ color: 'var(--grey-dark)' }}>
            This will add {pendingConfirmation.newTerms.join(', ')} to these books.
          </p>
        )}

        <div className="flex gap-3">
          <button
            onClick={() => void confirmAction()}
            disabled={isProcessing}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 disabled:opacity-50"
            style={{
              backgroundColor: 'var(--primary)',
              color: '#FFFFFF',
            }}
          >
            {isProcessing ? (
              <span className="flex items-center justify-center gap-2">
                <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Saving...
              </span>
            ) : (
              'Confirm'
            )}
          </button>
          <button
            onClick={cancelAction}
            disabled={isProcessing}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 disabled:opacity-50"
            style={{
              backgroundColor: 'var(--grey-light)',
              color: 'var(--dark)',
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationCard;
